import { useSnackbar } from "../use-snackbar";
import useReservationService from "../use-reservation-service";
import useAnalytics from "../use-analytics";
import { useSession } from "../use-session";
import { useAtomState } from "../use-atom";
import { useCallback, useEffect, useRef } from "react";

function usePriceCalculator(
  id: string,
  property_slug: string,
  checkin_date: Date | null,
  checkout_date: Date | null,
  adult_count: number,
  child_count: number,
  coupon_code: string,
  property_configs: PropertyConfigArg[],
  avail_loyalty_points?: boolean,
  couponSource?: "backend" | "manual" | "list" | null,
  display_protect_widget?: boolean,
  is_refundable_opted?: boolean,
  protect_quote_id?: string,
) {
  const enqueueSnackbar = useSnackbar(),
    { service } = useReservationService(),
    { track } = useAnalytics(),
    session = useSession(),
    [isLoading, setIsLoading] = useAtomState(`${id}-loading`, false),
    [tentativePrice, setTentativePrice] = useAtomState(id, DEFAULT_TENTATIVE_PRICE),

    // Track previous state
    prevStateRef = useRef<PriceCalculatorState | null>(null),

    // State comparison function
    hasStateChanged = useCallback(
      (currentState: PriceCalculatorState) => {
        if (!prevStateRef.current) return true;
        return JSON.stringify(prevStateRef.current) !== JSON.stringify(currentState);
      }, 
    []),

    handleGetTentativePrice = useCallback(
      (
        property_slug: string,
        checkin_date: Date | null,
        checkout_date: Date | null,
        adult_count: number,
        child_count: number,
        coupon_code: string,
        property_configs: PropertyConfigArg[],
        avail_loyalty_points?: boolean,
        display_protect_widget?: boolean,
        is_refundable_opted?: boolean,
        protect_quote_id?: string,
      ) => {
        let cancelled = false;

        (async () => {
          const trackingPayload: any = {
            property_slug: property_slug,
            checkin_date: checkin_date,
            checkout_date: checkout_date,
            adult_count: adult_count,
            child_count: child_count,
            coupon_code: coupon_code,
            avail_loyalty_points: avail_loyalty_points,
            display_protect_widget: display_protect_widget,
            is_refundable_opted: is_refundable_opted,
            protect_quote_id: protect_quote_id,
          };

          if (
            (checkin_date && !checkout_date)
            || (!checkin_date && checkout_date)
          ) {
            track(
              "rate_calculator_config_change",
              {
                ...trackingPayload,
                error: "incomplete date selection",
              },
            );

            return;
          }

          setIsLoading(true);

          const { error, response } = await service.getTentativePrice(
            {
              property_slug: property_slug,
              checkin_date: checkin_date?.toISOString(),
              checkout_date: checkout_date?.toISOString(),
              adult_count: adult_count,
              child_count: child_count,
              coupon_code: coupon_code,
              avail_loyalty_points: avail_loyalty_points,
              property_configs: property_configs,
              display_protect_widget: display_protect_widget,
              is_refundable_opted: is_refundable_opted,
              protect_quote_id: protect_quote_id,
            },
            session,
          );

          if (cancelled) {
            setIsLoading(false);
            response ? setTentativePrice(response) : null;
            return;
          }

          if (error) {
            enqueueSnackbar(error.message, "error");

            track(
              "rate_calculator_config_change",
              {
                ...trackingPayload,
                error: error.message,
              },
            );
            setTentativePrice(DEFAULT_TENTATIVE_PRICE);
            setIsLoading(false);

            return;
          }

          if (!response) {
            setTentativePrice(DEFAULT_TENTATIVE_PRICE);

            track(
              "rate_calculator_config_change",
              {
                ...trackingPayload,
                error: "unknown error",
              },
            );
            setIsLoading(false);

            return;
          }

          track(
            "rate_calculator_config_change",
            {
              ...trackingPayload,
              ...response,
            },
          );

          setTentativePrice(response);
          setIsLoading(false);
        })();

        return () => {
          cancelled = true;
        };
      },
      [enqueueSnackbar, setTentativePrice, session],
    );

  useEffect(
    () => {
      // Prevent API call if couponSource is "backend"
      if (couponSource === "backend") {
        // Do not trigger price calculation when couponSource is backend,
        // as this would cause a duplicate API call after backend coupon auto-application.
        return;
      }

      // Prepare the state for change detection.
      const currentState: PriceCalculatorState = {
        property_slug,
        checkin_date,
        checkout_date,
        adult_count,
        child_count,
        coupon_code,
        property_configs,
        avail_loyalty_points,
        display_protect_widget,
        is_refundable_opted,
        protect_quote_id,
      };
      
      // Only trigger price calculation if the state has changed.
      if (hasStateChanged(currentState)) {
        prevStateRef.current = currentState;
        return handleGetTentativePrice(
          property_slug,
          checkin_date,
          checkout_date,
          adult_count,
          child_count,
          coupon_code,
          property_configs,
          avail_loyalty_points,
          display_protect_widget,
          is_refundable_opted,
          protect_quote_id,
        );
      }
      return undefined;
    },
    [
      property_slug,
      checkin_date,
      checkout_date,
      adult_count,
      child_count,
      coupon_code,
      avail_loyalty_points,
      property_configs,
      couponSource,
    ],
  );

  return {
    tentativePrice: tentativePrice,
    isPriceLoading:isLoading,
  };
}

function usePriceCalculatorValue(id: string) {
  const [tentativePrice] = useAtomState(id, DEFAULT_TENTATIVE_PRICE);
  const [isLoading] = useAtomState(`${id}-loading`, false);

  return {
    tentativePrice: tentativePrice,
    isPriceLoading: isLoading,
  };
}
export default usePriceCalculator;

export {
  usePriceCalculatorValue,
};

const DEFAULT_TENTATIVE_PRICE: TentativePrice = {
  breakdown: {
    average_base_rate:0,
    average_non_discounted_rate:0,
    discount_value:0,
    gross_base_rate: 0,
    gross_base_rate_without_extras: 0,
    taxes: 0,
    convenience_fee: 0,
    deposit: 0,
    extra_adults: 0,
    extra_adults_fare: 0,
    rental_charges: 0,
    discount: 0,
    coupon_or_tier_discount:0,
    discount_message: "",
    total: 0,
    discount_method: "",
    discount_type: "",
    coupon_code: "",
    loyalty_points_discount_percentage: 0,
    loyalty_points_discount_percentage_value: 0,
    loyalty_points_redeemed_value: 0,
    loyalty_points_earning: 0,
    premium: 0,
  },
  widget_details: {
    protectHtml: "",
    protectQuoteId: "",
  },
  coupon_discount:0,
  tax_percentage: 0,
  total_taxes:0,
  average_base_rate:0,
  average_non_discounted_rate:0,
  discount_value:0,
  discount:0,
  coupon_or_tier_discount:0,
  tier_discount_percentage: 0,
  tier_discount: 0,
  currency_code: "",
  currency_symbol: "",
  discounted_total: 0,
  property_configs: [],
  sold_out: false,
  status: "",
  total: 0,
};

// Add this type at the top of the file
type PriceCalculatorState = {
  property_slug: string;
  checkin_date: Date | null;
  checkout_date: Date | null;
  adult_count: number;
  child_count: number;
  coupon_code: string;
  property_configs: PropertyConfigArg[];
  avail_loyalty_points?: boolean;
  display_protect_widget?: boolean;
  is_refundable_opted?: boolean;
  protect_quote_id?: string;
};